import {Alert, Box, Divider, Stack, Tooltip, Typography} from "@mui/material"
import HomeIcon from '@mui/icons-material/Home';
import DialogAddress from "./DialogAddress";
import {Fragment, useEffect, useState} from "react";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import toast from "react-hot-toast";
import {AddressFormatter} from "../../../../../utils/formatters/addressFormatter";
import {FontInputData, FontNotInputData, FontSubtitleTwo} from "../../../components/SharedTypography";
import {
    Client,
    ClientAddress,
    ClientAddressFields,
    ClientAddressTypes,
    ClientFields
} from "../../../../../types/client/clientData";
import {BotonEditar} from "../../../components/Buttons";
import {HttpClientAddress} from "../../../../../http/client/httpClient";
import {TipoDomicilio} from "../../../models/enums";
import {EntityWithIdFields} from "../../../../../types/baseEntities";

const TEXTOS = {
    DOMICILIOS: 'Domicilios',
    DOMICILIO_FISCAL: "Domicilio Fiscal (Dirección de inscripción de AFIP)",
    DOMICILIO_ACTIVIDAD: "Domicilio de Actividad",
    DOMICILIO_REAL: "Domicilio Real",
    PENDIENTE_DE_CARGA: "Pendiente de carga",
    RECORDATORIO: "Recuerde que para acceder a una garantía FOGABA deberá contar con domicilio fiscal o de actividad en Provincia de Buenos Aires, o contar con convenio multilateral."
}

const fuenteMain = (texto: string) => <Typography variant='subtitle1' fontWeight={550}>{texto}</Typography>

interface AddressesProps {
    client: Client,
    canEdit?: boolean,
    hideCheck?: boolean
}

const Addresses = ({client, canEdit, hideCheck}: AddressesProps) => {
    const [editAddress, setEditAddress] = useState<boolean>(false)
    const [addresses, setAddresses] = useState<ClientAddress[]>()
    const [addressInDialog, setAddressInDialog] = useState<ClientAddress | undefined>(undefined)
    const [addressType, setAddressType] = useState<ClientAddressTypes | undefined>(undefined)
    
    const loadAddresses = () => {HttpClientAddress.getByIdClient(client[EntityWithIdFields.Id]).then((r) => setAddresses(r))}

    useEffect(() => {
        loadAddresses()
    }, [client])

    const DireccionFiscalCargada = ({ direccion, tipo, hideCheck }: { direccion: ClientAddress, tipo: string, hideCheck?: boolean }) => {

        const showDireccion = AddressFormatter.toFullAddress(direccion)

        return (
            hideCheck ?
                <Box sx={{ backgroundColor: '#bceff5', borderRadius: '10px', p: 3, width: '100%' }}>
                    <Stack direction={'row'} alignItems={'center'} spacing={3}>
                        <HomeIcon fontSize={'large'} color={'info'} />
                        <Stack>
                            <Typography variant='subtitle1' fontWeight={550} color={'black !important'}>{tipo}</Typography>
                            <FontInputData
                                text={showDireccion}
                            />
                        </Stack>
                    </Stack>
                </Box>
                    :
                <Box sx={{ backgroundColor: '#bceff5', borderRadius: '10px', p: 3, width: '100%' }}>
                    <Stack direction={'row'} alignItems={'center'} spacing={3}>
                        <HomeIcon fontSize={'large'} color={'info'} />
                        <Stack>
                            <Typography variant='subtitle1' fontWeight={550} color={'black !important'}>{tipo}</Typography>
                            <FontInputData
                                text={showDireccion}
                            />
                        </Stack>
                    </Stack>
                </Box>
        )
    }

    const DireccionPendiente = ({ tipo, handleEdit }: { tipo: string, handleEdit?: () => void }) => {

        return (
            <Fragment>
                <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
                    <Stack>
                        {fuenteMain(tipo)}
                        <FontNotInputData text={TEXTOS.PENDIENTE_DE_CARGA} />
                    </Stack>
                    {
                        handleEdit && <BotonEditar onClick={handleEdit} />
                    }
                </Stack>
            </Fragment>
        )
    }

    const handleEditReal = (realAddress?: ClientAddress) => {
        setEditAddress(true)
        setAddressType(ClientAddressTypes.Real)
        setAddressInDialog(realAddress)
    }

    const handleEditActivity = (activityAddress?: ClientAddress) => {
        setEditAddress(true)
        setAddressType(ClientAddressTypes.Actividad)
        setAddressInDialog(activityAddress)
    }
    
    const handleEditLegal = (legalAddress?: ClientAddress) => {
        setEditAddress(true)
        setAddressType(ClientAddressTypes.Legal)
        setAddressInDialog(legalAddress)
    }

    const renderAddressTitle = () => {
        if (addressType === ClientAddressTypes.Actividad) return 'Editar domicilio de Actividad'
        if (addressType === ClientAddressTypes.Real) return 'Editar domicilio Real'
        if (addressType === ClientAddressTypes.Legal) return 'Editar domicilio Legal'
        else return ''
    }

    const domicilioFiscal = (fiscalAddress?: ClientAddress) => {
        if (fiscalAddress) {
            return <DireccionFiscalCargada
                direccion={fiscalAddress}
                tipo={TipoDomicilio.FISCAL}
                hideCheck={hideCheck}
            />
        }
    }

    const domicilioReal = (realAddress?: ClientAddress) => {
        if (realAddress) {
            if (client[ClientFields.ClientTypeCode] === 1) {
                return (
                    <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
                        <Stack>
                            {fuenteMain(TipoDomicilio.REAL)}
                            <FontInputData text={AddressFormatter.toFullAddress(realAddress)} />
                        </Stack>
                        {
                            canEdit &&
                            <BotonEditar onClick={() => {handleEditReal(realAddress)}} />
                        }
                    </Stack>
                )
            }
        }else{
            if (client[ClientFields.ClientTypeCode] === 1) {
                return <DireccionPendiente
                    handleEdit={() => {handleEditReal(undefined)}}
                    tipo={TipoDomicilio.REAL}
                />
            }
        }
    }

    const domicilioActividad = (activityAddress?: ClientAddress) => {
        if (activityAddress) {
            return (
                    <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
                        <Stack>
                            {fuenteMain(TipoDomicilio.ACTIVIDAD)}
                            <FontInputData text={AddressFormatter.toFullAddress(activityAddress)} />
                        </Stack>
                        {
                            canEdit &&
                            <BotonEditar onClick={() => {handleEditActivity(activityAddress)}} />
                        }
                    </Stack>
            )
            
        }else{
            return <DireccionPendiente
                    handleEdit={() => {handleEditActivity(undefined)}}
                    tipo={TipoDomicilio.ACTIVIDAD}
            />
        }
    }

    const domicilioLegal = (legalAddress?: ClientAddress) => {
        if (legalAddress && client[ClientFields.ClientTypeCode] !== 1) {
            return (
                <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
                    <Stack>
                        {fuenteMain(TipoDomicilio.LEGAL)}
                        <FontInputData text={AddressFormatter.toFullAddress(legalAddress)} />
                    </Stack>
                    {
                        canEdit &&
                        <BotonEditar onClick={() => {handleEditLegal(legalAddress)}} />
                    }
                </Stack>
            )

        }else{
            return <DireccionPendiente
                handleEdit={() => {handleEditLegal(undefined)}}
                tipo={TipoDomicilio.LEGAL}
            />
        }
    }
    
    const onCloseDialog = () => {
        setEditAddress(false)
        setAddressInDialog(undefined)
    }
    
    
    const onSubmitDialog = () => {
        toast.success("La dirección fue guardada con éxito")
        loadAddresses()
    }

    return (
        <Fragment>
            {
                addresses && addresses.length !== 0 &&
                <Fragment>
                    <Stack spacing={4}>
                        <Stack direction={'row'} alignItems='center' spacing={2}>
                            <FontSubtitleTwo text={TEXTOS.DOMICILIOS} />
                            <Tooltip title={"El Domicilio Fiscal se cargó automáticamente desde AFIP. Ante cualquier inquietud, debe editarlo en dicha plataforma"}>
                                <InfoOutlinedIcon fontSize={"small"} color="info"/>
                            </Tooltip>
                        </Stack>
                        <Divider />
                        {
                            canEdit &&
                            <Alert severity='warning'>
                                {TEXTOS.RECORDATORIO}
                            </Alert>
                        }
                        <Stack spacing={4}>
                            {domicilioFiscal(addresses.find((a) => a[ClientAddressFields.AddressTypeCode] === ClientAddressTypes.Fiscal))}
                            {domicilioReal(addresses.find((a) => a[ClientAddressFields.AddressTypeCode] === ClientAddressTypes.Real))}
                            {domicilioActividad(addresses.find((a) => a[ClientAddressFields.AddressTypeCode] === ClientAddressTypes.Actividad))}
                            {domicilioLegal(addresses.find((a) => a[ClientAddressFields.AddressTypeCode] === ClientAddressTypes.Legal))}
                        </Stack>
                    </Stack>
                    {
                        canEdit &&
                        <DialogAddress
                            open={editAddress}
                            setEditAddress={setEditAddress}
                            renderAddressTitle={renderAddressTitle}
                            address={addressInDialog && addressInDialog}
                            onClose={onCloseDialog}
                            onSubmitDialog={onSubmitDialog}
                            clientId={client[EntityWithIdFields.Id]}
                            addressType={addressType}
                        />
                    }
                </Fragment>
            }
        </Fragment>
    )
}


export default Addresses